/* eslint-disable react/prop-types */
import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import ReactTooltip from "react-tooltip"

import ErrorGroup from "areas/product/error-group"
import withEngravings from "areas/product/hoc/with-engravings"
import {
  GradientContainer,
  ChoiceOption,
  OptionsSelect,
  OptionsSlider,
  OptionTitle,
} from "areas/product/wizard-options/components"
import Image from "components/ui/image"
import { imageLookup } from "helpers/product/image-lookup"
import { filterProductOptions } from "helpers/product/product-filtering"
import { useNavigation } from "helpers/use-navigation"
import { cn } from "styles/utils"

import EngravingOptions from "./engraving-options"
import Entry from "./entry"

const ChoiceOptions = ({
  data,
  pref,
  modal,
  choices,
  selectedFont,
  setFont,
}) => {
  const keyboardProps = useNavigation({ direction: "horizontal" })

  return (
    <OptionsSlider
      aria-label={`${data.heading.title} Options`}
      className={cn(modal && "-mx-24 px-24")}
      data-slider="true"
      role="radiogroup"
    >
      {filterProductOptions(data.options, choices, true).map((ef, index) => {
        const img = imageLookup(pref, ef.imageKey, "retina")
        const selected = selectedFont == ef.value
        return (
          <ChoiceOption
            aria-checked={selected}
            key={pref + index}
            onClick={() => setFont(data.pref, ef)}
            role="radio"
            style={{ maxWidth: "80px" }}
            tabIndex={selected ? 0 : -1}
            {...keyboardProps}
          >
            <Image
              alt=""
              height="40"
              sizes="40px"
              src={img.x2}
              srcSet={img.x1 + " 1x," + img.x2 + " 2x"}
              title={ef.display_name}
              width="40"
            />
            <OptionTitle>{ef.display_name}</OptionTitle>
          </ChoiceOption>
        )
      })}
    </OptionsSlider>
  )
}

ChoiceOptions.propTypes = {
  choices: PropTypes.object,
  data: PropTypes.object,
  modal: PropTypes.bool,
  pref: PropTypes.string,
  selectedFont: PropTypes.string,
  setFont: PropTypes.func,
}

const Engravings = (props) => {
  const {
    adminMode,
    choices,
    contentOnly = false,
    data,
    disabledEngravings,
    errorText,
    images,
    isMobile,
    modal,
    pref,
    selectedEngCount,
    selectedFont,
    setEngCount,
    setFont,
    styleCode,
  } = props
  if (!data || data.disabled) {
    return null
  }
  let ef_out = []
  for (var i = 0; i < data.ef_order.length; i++) {
    let ef_mode = data.ef_order[i]
    switch (ef_mode) {
      case "text":
        filterProductOptions(data.engravings, choices, true)
          .filter(
            (engraving) => disabledEngravings.indexOf(engraving.pref) === -1
          )
          .forEach((engraving, index) => {
            if (engraving.ui_type == "dropdown") {
              ef_out.push(
                <EngravingOptions
                  engraving={engraving}
                  key={`engr-dropdown-${styleCode}-${engraving.pref}`}
                />
              )
            } else {
              let key_suffix = ""
              Object.keys(engraving.filters).forEach((key) => {
                key_suffix +=
                  key +
                  "-" +
                  engraving.filters[key].map((item) => item.value).join("-") +
                  "-"
              })
              const key = `engr-${styleCode}-${engraving.pref}${
                key_suffix && `-${key_suffix.slice(0, -1)}`
              }`
              ef_out.push(
                <div key={key}>
                  {data.display_labels &&
                    data.display_labels[engraving.pref] && (
                      <h5 className={cn("text-14 mb-8", index > 0 && "mt-32")}>
                        {data.display_labels[engraving.pref]}:
                      </h5>
                    )}
                  <ReactTooltip
                    id={`wizard-category__tip_${index}`}
                    place="right"
                  >
                    <div>
                      <span style={{ textTransform: "none" }}>
                        {engraving.tooltip}
                      </span>
                    </div>
                  </ReactTooltip>
                  <Entry
                    adminMode={adminMode}
                    data={data}
                    engraving={engraving}
                    index={index}
                    modal={modal}
                    styleCode={styleCode}
                  />

                  {data.no_engraving_preview &&
                    images[`preview_${engraving.engraving_num}`] && (
                      <Image
                        alt={`Preview for ${engraving.title}`}
                        height="100"
                        src={`${
                          images[`preview_${engraving.engraving_num}`].src
                        }&fmt=png-alpha`}
                        width="512"
                      />
                    )}
                </div>
              )
            }
          })
        break
      case "font":
        if (filterProductOptions(data.options, choices, true).length > 1) {
          ef_out.push(
            <GradientContainer key={pref}>
              <FocusScope>
                <ChoiceOptions
                  choices={choices}
                  data={data}
                  modal={modal}
                  pref={pref}
                  selectedFont={selectedFont}
                  setFont={setFont}
                />
                {!!data.allow_emojis && isMobile && (
                  <p className="text-12 mt-16">
                    You can add a &#x2764; emoji to your engraving!
                  </p>
                )}
              </FocusScope>
            </GradientContainer>
          )
        }
        break
      default:
    }
  }

  let ec_options = []
  if (data.explicit_engraving_count) {
    for (let i = data.min_engraving_count; i <= data.max_engraving_count; i++) {
      ec_options.push(
        <option key={i} value={i}>
          {i} Engraving
          {i > 1 ? "s" : ""}
        </option>
      )
    }
  }

  const content = (
    <div>
      {data.explicit_engraving_count && (
        <div>
          <p className="text-10 mb-4">Engraving Count</p>
          <OptionsSelect
            className="mb-16"
            onChange={(e) => {
              setEngCount(e)
            }}
            value={selectedEngCount}
          >
            {ec_options}
          </OptionsSelect>
        </div>
      )}
      {ef_out}
    </div>
  )

  if (contentOnly) {
    return content
  } else {
    return (
      <div>
        {errorText && <ErrorGroup>{errorText}</ErrorGroup>}
        <div className={cn("px-16 pb-16", modal && "px-0 pb-0")}>{content}</div>
      </div>
    )
  }
}

Engravings.propTypes = {
  choices: PropTypes.object,
  data: PropTypes.object,
  modal: PropTypes.bool,
  pref: PropTypes.string,
}

export default withEngravings(Engravings)
